// src/pages/seasons/RSVPPage.jsx
// Public RSVP page — no login required.
// URL: /rsvp/:sessionId/:registrationId

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  doc, getDoc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { format } from 'date-fns';
import { es as esLocale } from 'date-fns/locale';
import clsx from 'clsx';
import {
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  MapPinIcon,
  ClockIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid } from '@heroicons/react/24/solid';
import { setAppLanguage } from '@/i18n';

// Match the parent-facing surface (RegisterPage, ParentPortal,
// GameRsvpPage). RSVPPage is a sister to GameRsvpPage but for older
// session-based events; same brand identity.
const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ------------------------------------------------------------------ */
/* Bilingual strings                                                    */
/* ------------------------------------------------------------------ */
const T = {
  en: {
    linkExpired: 'Link Expired',
    errSession: 'Session not found.',
    errReg: 'Registration not found.',
    errGeneric: 'Something went wrong loading this page.',
    sessionFallback: 'Session',
    rsvpFor: 'RSVP for',
    playerFallback: 'Player',
    savedConfirm: 'Response recorded — you can change it below.',
    noteLabel: 'Note for coach',
    noteOptional: '(optional)',
    notePlaceholder: 'e.g. Running 10 min late, or injured ankle',
    updateNote: 'Update Note',
    saving: 'Saving…',
    coachNote: 'Coach Note',
    poweredBy: 'Powered by',
    optGoing: 'Going',
    optMaybe: 'Maybe',
    optNotGoing: 'Not Going',
    sessionType: {
      Practice: 'Practice',
      Game: 'Game',
      Friendly: 'Friendly',
      Tournament: 'Tournament',
      Other: 'Session',
    },
  },
  es: {
    linkExpired: 'Enlace Expirado',
    errSession: 'Sesión no encontrada.',
    errReg: 'Registro no encontrado.',
    errGeneric: 'Algo salió mal al cargar la página.',
    sessionFallback: 'Sesión',
    rsvpFor: 'Confirmar asistencia de',
    playerFallback: 'Jugador',
    savedConfirm: 'Respuesta guardada — puedes cambiarla abajo.',
    noteLabel: 'Nota para el entrenador',
    noteOptional: '(opcional)',
    notePlaceholder: 'ej. Llegaré 10 min tarde, o lesión en el tobillo',
    updateNote: 'Actualizar Nota',
    saving: 'Guardando…',
    coachNote: 'Nota del Entrenador',
    poweredBy: 'Con tecnología de',
    optGoing: 'Asistiré',
    optMaybe: 'Tal vez',
    optNotGoing: 'No asistiré',
    sessionType: {
      Practice: 'Entrenamiento',
      Game: 'Partido',
      Friendly: 'Amistoso',
      Tournament: 'Torneo',
      Other: 'Sesión',
    },
  },
};

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

function formatTime(timeStr, lang = 'en') {
  if (!timeStr) return null;
  try {
    const [h, min] = timeStr.split(':');
    const d = new Date();
    d.setHours(+h, +min);
    const locale = lang === 'es' ? 'es-ES' : 'en-US';
    return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  } catch { return timeStr; }
}

const TYPE_EMOJI = {
  Practice: '⚽', Game: '🏆', Friendly: '🥅', Tournament: '🎯', Other: '📋',
};

// Three-state RSVP: Going / Maybe / Not Going.
//   Going    → lime (primary brand action, "yes please")
//   Maybe    → amber (semantic "kind of yes", reads as soft commitment)
//   Not Going → red (semantic "no")
// Active state inverts the fill so the picked option stands out at a
// glance after submit. Same selectable-card pattern used everywhere
// else in the parent surface.
const RSVP_OPTIONS = [
  {
    value: 'going',
    labelKey: 'optGoing',
    emoji: '✅',
    bg: 'bg-white border-zinc-200 text-[#0d1f14] hover:border-[#b5f03d]/40 hover:bg-[#b5f03d]/5',
    activeBg: 'bg-[#b5f03d] border-[#b5f03d] text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]',
    icon: CheckCircleIcon,
  },
  {
    value: 'maybe',
    labelKey: 'optMaybe',
    emoji: '🤔',
    bg: 'bg-white border-zinc-200 text-[#0d1f14] hover:border-amber-300 hover:bg-amber-50/50',
    activeBg: 'bg-amber-500 border-amber-500 text-white shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]',
    icon: QuestionMarkCircleIcon,
  },
  {
    value: 'not_going',
    labelKey: 'optNotGoing',
    emoji: '❌',
    bg: 'bg-white border-zinc-200 text-[#0d1f14] hover:border-red-300 hover:bg-red-50/50',
    activeBg: 'bg-red-500 border-red-500 text-white shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]',
    icon: XCircleIcon,
  },
];

/* ------------------------------------------------------------------ */
/* Main component                                                       */
/* ------------------------------------------------------------------ */
export default function RSVPPage() {
  const { sessionId, registrationId } = useParams();
  const { i18n } = useTranslation();
  const lang = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const tr = T[lang];

  const [session,      setSession]      = useState(null);
  const [registration, setRegistration] = useState(null);
  const [existing,     setExisting]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState(null);
  const [note,         setNote]         = useState('');
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [error,        setError]        = useState(null);

  /* Load session + registration + existing RSVP */
  useEffect(() => {
    async function load() {
      try {
        const [sessSnap, regSnap, rsvpSnap] = await Promise.all([
          getDoc(doc(db, 'sessions', sessionId)),
          getDoc(doc(db, 'registrations', registrationId)),
          getDoc(doc(db, 'rsvps', `${sessionId}_${registrationId}`)),
        ]);

        if (!sessSnap.exists()) { setError(tr.errSession); setLoading(false); return; }
        if (!regSnap.exists())  { setError(tr.errReg);     setLoading(false); return; }

        setSession({ id: sessSnap.id, ...sessSnap.data() });
        setRegistration({ id: regSnap.id, ...regSnap.data() });

        if (rsvpSnap.exists()) {
          const rsvp = rsvpSnap.data();
          setExisting(rsvp);
          setSelected(rsvp.status);
          setNote(rsvp.note || '');
          setSaved(true);
        }
      } catch (err) {
        console.error(err);
        setError(tr.errGeneric);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [sessionId, registrationId]);

  const handleSubmit = async (statusValue) => {
    setSelected(statusValue);
    setSaving(true);
    try {
      await setDoc(doc(db, 'rsvps', `${sessionId}_${registrationId}`), {
        sessionId,
        registrationId,
        seasonId:    registration?.seasonId   || null,
        teamId:      registration?.teamId     || null,
        playerName:  registration?.playerName || '',
        parentName:  registration?.parentName || '',
        status:      statusValue,
        note:        note.trim() || null,
        updatedAt:   serverTimestamp(),
      }, { merge: true });
      setSaved(true);
      setExisting({ status: statusValue, note: note.trim() || null });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[#5a8a1f] border-t-transparent animate-spin" />
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center px-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] border border-zinc-200 p-8 text-center">
          <XCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <p
            className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {tr.linkExpired}
          </p>
          <p className="text-sm text-zinc-600 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const sessionDate = toDate(session?.date);
  const timeStr     = formatTime(session?.time, lang);
  const typeEmoji   = TYPE_EMOJI[session?.type] || '📋';
  const playerName  = registration?.playerName || tr.playerFallback;
  const teamName    = registration?.teamName   || '';
  const dateLocale  = lang === 'es' ? { locale: esLocale } : undefined;
  const typeLabel   = (session?.type && tr.sessionType[session.type]) || tr.sessionFallback;

  return (
    <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">

        {/* Language toggle — same pill pattern as RegisterPage. */}
        <div className="flex justify-end mb-3">
          <button
            onClick={() => setAppLanguage(lang === 'en' ? 'es' : 'en')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-xs font-bold uppercase text-zinc-600 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d] hover:text-[#5a8a1f] transition-colors"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <span>{lang === 'en' ? '🇲🇽' : '🇺🇸'}</span>
            {lang === 'en' ? 'Español' : 'English'}
          </button>
        </div>

        {/* Scrimly wordmark — lime "Scrim" + pitch "ly", same shape as
            in-app branding. */}
        <div className="text-center mb-6">
          <span
            className="text-2xl font-bold uppercase tracking-tight"
            style={{ fontFamily: HEAD_FONT }}
          >
            <span className="text-[#5a8a1f]">Scrim</span><span className="text-[#0d1f14]">ly</span>
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] border border-zinc-200 overflow-hidden">

          {/* Session header — pitch-dark with lime grid overlay, same
              treatment as the GameRsvpPage hero so a parent landing here
              from any RSVP path sees the same brand identity. */}
          <div
            className="relative overflow-hidden px-6 py-5 text-white"
            style={{
              backgroundColor: '#0d1f14',
              backgroundImage:
                'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          >
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">{typeEmoji}</span>
                <span
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {typeLabel}
                </span>
              </div>
              {teamName && (
                <p
                  className="text-white text-lg font-bold uppercase mb-3"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {teamName}
                </p>
              )}
              <div className="space-y-1.5">
                {sessionDate && (
                  <div className="flex items-center gap-2 text-sm text-zinc-200">
                    <CalendarDaysIcon className="h-4 w-4 shrink-0" />
                    {format(sessionDate, 'EEEE, MMMM d, yyyy', dateLocale)}
                  </div>
                )}
                {timeStr && (
                  <div className="flex items-center gap-2 text-sm text-zinc-200">
                    <ClockIcon className="h-4 w-4 shrink-0" />
                    {timeStr}
                  </div>
                )}
                {session?.location?.name && (
                  <div className="flex items-center gap-2 text-sm text-zinc-200">
                    <MapPinIcon className="h-4 w-4 shrink-0" />
                    {session.location.name}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            <p
              className="text-center text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.rsvpFor}
            </p>
            <p
              className="text-center text-xl font-bold uppercase tracking-tight text-[#0d1f14] mb-5"
              style={{ fontFamily: HEAD_FONT }}
            >
              {playerName}
            </p>

            {/* Saved confirmation — lime-themed banner. */}
            {saved && (
              <div className="flex items-center gap-2 rounded-xl bg-[#b5f03d]/10 border border-[#b5f03d]/40 px-4 py-2.5 mb-4">
                <CheckSolid className="h-4 w-4 text-[#5a8a1f] shrink-0" />
                <p className="text-sm font-semibold text-[#3d6010]">
                  {tr.savedConfirm}
                </p>
              </div>
            )}

            {/* RSVP buttons */}
            <div className="space-y-3">
              {RSVP_OPTIONS.map((opt) => {
                const isActive = selected === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSubmit(opt.value)}
                    disabled={saving}
                    className={clsx(
                      'w-full flex items-center gap-3 rounded-2xl border-2 px-5 py-3.5 font-bold uppercase text-sm transition-all',
                      isActive ? opt.activeBg : opt.bg,
                      saving && 'opacity-60 cursor-not-allowed',
                    )}
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  >
                    <span className="text-lg">{opt.emoji}</span>
                    <span className="flex-1 text-left">{tr[opt.labelKey]}</span>
                    {isActive && saving && (
                      <div className={clsx(
                        'h-4 w-4 rounded-full border-2 border-t-transparent animate-spin',
                        opt.value === 'going' ? 'border-[#0d1f14]' : 'border-white',
                      )} />
                    )}
                    {isActive && !saving && (
                      <CheckSolid className="h-4 w-4" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Optional note */}
            <div className="mt-4">
              <label
                className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1.5"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.noteLabel} <span className="font-normal text-zinc-400 normal-case tracking-normal">{tr.noteOptional}</span>
              </label>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={tr.notePlaceholder}
                className="w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 resize-none"
              />
              {note && selected && (
                <button
                  type="button"
                  onClick={() => handleSubmit(selected)}
                  disabled={saving}
                  className="mt-2 w-full rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] transition-all disabled:opacity-50"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {saving ? tr.saving : tr.updateNote}
                </button>
              )}
            </div>

            {session?.note && (
              <div className="mt-4 rounded-xl bg-zinc-50 border border-zinc-200 px-4 py-3">
                <p
                  className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#5a8a1f] mb-1"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {tr.coachNote}
                </p>
                <p className="text-sm text-[#0d1f14]">{session.note}</p>
              </div>
            )}
          </div>
        </div>

        <p
          className="text-center text-xs text-zinc-500 mt-5"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          {tr.poweredBy} <span className="font-bold uppercase text-[#5a8a1f]">Scrimly</span>
        </p>
      </div>
    </div>
  );
}
